// PowerSync — metadata audio_recaps (без script_text/script_segments — их нет в sync).
import { usePowerSyncQuery } from "@/lib/powersync/use-query";
import { belongsToLiveMaterial } from "../live-material-sql";

export interface LocalAudioRecap {
	id: string;
	user_id: string;
	title: string;
	material_id: string | null;
	recap_format: string | null;
	voice_mode: string | null;
	language: string | null;
	duration_sec: number | null;
	/** Заказанная длительность — для пометки «короче заказанного» в списке/детали. */
	target_duration_min: number | null;
	status: string;
	/**
	 * Код причины провала (`sanitizeAiErrorForClient`), не текст модели. Нужен
	 * экрану детали: без него провал всегда выглядел как безымянное «не удалось
	 * сгенерировать», хотя устранимую причину сервер называет точно.
	 */
	failure_reason: string | null;
	created_at: string;
	updated_at: string;
	ready_at: string | null;
	deleted_at: string | null;
	preview_key: string | null;
}

export function useAudioRecapsQuery(userId: string | undefined) {
	// Резюме удалённого материала уходит в корзину вместе с ним (серверный
	// каскад), но каскад доезжает только следующим кругом синка — до этого
	// список показывал резюме уже удалённого источника. Предикат общий, см.
	// `live-material-sql`. `material_id IS NULL` — сборка по нескольким
	// материалам (`material_ids`), такие резюме живут своей жизнью.
	return usePowerSyncQuery<LocalAudioRecap>(
		`SELECT r.id, r.user_id, r.title, r.material_id, r.recap_format, r.voice_mode,
		        r.language, r.duration_sec, r.target_duration_min, r.status, r.failure_reason,
		        r.created_at, r.updated_at, r.ready_at, r.deleted_at, r.preview_key
		   FROM audio_recaps r
		  WHERE r.user_id = ? AND r.deleted_at IS NULL AND ${belongsToLiveMaterial("r")}
		ORDER BY r.created_at DESC`,
		[userId ?? ""],
	);
}

/**
 * Одна запись по id — офлайн-фолбэк детального экрана.
 *
 * Колонки те же, что у списка: транскрипт (`script_segments` / `script_text`) в
 * стрим `user_audio_recaps` не входит и офлайн недоступен физически, а вот
 * заголовок, длительность и статус нужны, чтобы показать плеер и проиграть
 * скачанный файл без сети.
 */
export function useAudioRecapByIdQuery(id: string | undefined) {
	return usePowerSyncQuery<LocalAudioRecap>(
		`SELECT id, user_id, title, material_id, recap_format, voice_mode,
		        language, duration_sec, target_duration_min, status, failure_reason, created_at, updated_at,
		        ready_at, deleted_at, preview_key
		   FROM audio_recaps
		  WHERE id = ? AND deleted_at IS NULL
		  LIMIT 1`,
		[id ?? ""],
	);
}

export function useAudioRecapsByMaterialQuery(
	userId: string | undefined,
	materialId: string | undefined,
) {
	return usePowerSyncQuery<LocalAudioRecap>(
		`SELECT id, user_id, title, material_id, recap_format, voice_mode,
		        language, duration_sec, target_duration_min, status, failure_reason, created_at, updated_at,
		        ready_at, deleted_at, preview_key
		   FROM audio_recaps
		  WHERE user_id = ? AND material_id = ? AND deleted_at IS NULL
		ORDER BY created_at DESC`,
		[userId ?? "", materialId ?? ""],
	);
}
